import { useAuthorization } from 'dx-sdk/build/authorization';
import {
  CTAPlannerCard,
  Hero,
  Picture,
  Typography,
} from 'dx-sdk/build/components';
import { Theme as ThemeProvider } from 'dx-sdk/build/providers';
import { formatDealerName } from 'pubweb-smokey/dist/utils/formatters';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, Redirect } from 'react-router-dom';
import App from '../../../App';
import { DXAccountContext } from '../../../contexts/DXAccountContext';
import PlannerCTAIcon from '../../../images/portal/cta-planner-card-icon.PNG';
import plannerCardLinkIcon from '../../../images/portal/plannerCardLinkIcon.svg';
import plannerCTAAccent from '../../../images/portal/plannerLinkCTAAccent.svg';
import HomesForYouBadge from '../../../images/svg/HomesForYouBadge.svg';
import Loading from '../../../images/svg/icons/Loading';
import * as dealerService from '../../../services/dealerService';
import * as homesService from '../../../services/homesService';
import { fireSegmentEvent, parseInitialPropsContext } from '../../../utils';
import RecommendedHomes from '../../components/RecommendedHomes/RecommendedHomes';
import {
  createBedBathHeading,
  findBedBathHomes,
  findBudgetHomes,
  findFavoriteHomes,
  getFeaturesContent,
  roundBudgetNumber,
} from '../../utils/PortalUtils';
import HomesForYouStyles from './HomesForYou.styled';

const HomesForYou = ({ dealer, homes }) => {
  const dxContext = useContext(DXAccountContext);
  const favoriteList = dxContext?.state.favorites;
  const favoritesLoaded = dxContext?.state.favoritesLoaded;
  const favoriteHomes = findFavoriteHomes(favoriteList, homes);
  const [plannerHasBeenSubmitted, setPlannerHasBeenSubmitted] = useState(null);
  const [showFavoriteContent, setShowFavoriteContent] = useState(false);
  const roundedBudgetNumber = roundBudgetNumber(
    dxContext?.state?.account?.profile?.budget
  );
  const budgetHomes = findBudgetHomes(
    dxContext?.state?.account?.profile?.budget,
    homes
  );

  const budgetHomesList = useMemo(() => {
    if (!budgetHomes) {
      return null;
    }
    return budgetHomes.map((item) => {
      return {
        id: item.serialNumber || item.modelNumber,
        retailHomeObject: {
          ...item,
        },
      };
    });
  }, [budgetHomes]);

  useEffect(() => {
    if (favoritesLoaded && favoriteHomes && favoriteHomes.length > 0) {
      setShowFavoriteContent(true);
    }
  }, [favoritesLoaded]);

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem('planner_fs'));
    if (items) {
      setPlannerHasBeenSubmitted(true);
    } else {
      setPlannerHasBeenSubmitted(false);
    }
  }, []);

  const bedBathHeading = createBedBathHeading(
    dxContext?.state?.account?.profile?.preferredBeds,
    dxContext?.state?.account?.profile?.preferredBaths
  );

  const bedBathHomes = findBedBathHomes(
    dxContext?.state?.account?.profile?.preferredBeds,
    dxContext?.state?.account?.profile?.preferredBaths,
    homes
  );

  const bedBathHomesList = useMemo(() => {
    if (!bedBathHomes) {
      return null;
    }
    return bedBathHomes.map((item) => {
      return {
        id: item.serialNumber || item.modelNumber,
        retailHomeObject: {
          ...item,
        },
      };
    });
  }, [bedBathHomes]);

  const featuresContent = getFeaturesContent(
    dxContext?.state?.account?.profile?.likedFeatures,
    homes
  );

  const { isAuthenticated, isLoading } = useAuthorization();

  if (!isAuthenticated && !isLoading) {
    return <Redirect to="/myhome-account-login" />;
  }

  return (
    <ThemeProvider theme={{}}>
      <App dealer={dealer} homes={homes}>
        <HomesForYouStyles>
          <Helmet>
            <title>
              Homes For You | {formatDealerName(dealer?.dealerName)}
            </title>
            <meta name="robots" content="noindex" />
          </Helmet>

          {dxContext?.state?.globalLoading ? (
            <div className="loading-screen-panel">
              <Loading width="150px" />
            </div>
          ) : (
            <>
              <Hero
                accent={
                  <Picture src={HomesForYouBadge} alt="Homes For You Badge" />
                }
                heading="Homes For You"
                align="left"
                componentsProps={{
                  Container: {
                    style: {
                      maxWidth: '934px',
                    },
                  },
                }}
                content={
                  <>
                    <Typography
                      variant="intro"
                      className="large-p"
                      componentProps={{
                        style: {
                          margin: '0 0 7px 0',
                        },
                      }}
                    >
                      Homes for you are specially curated collections based on
                      your Planner preference selections. Heart your favorite
                      homes to save to your My Favorites list.
                    </Typography>
                    {showFavoriteContent && (
                      <>
                        <Typography
                          variant="caption"
                          className="small-p"
                          componentProps={{
                            style: {
                              margin: '0',
                            },
                          }}
                        >
                          Find all of your saved homes on your My Favorites
                          page.
                        </Typography>
                        <div className="button-wrap">
                          <Link
                            className="hero-btn"
                            to={{
                              pathname: '/portal/favorites',
                              state: { dealer, homes },
                            }}
                            onClick={() => {
                              fireSegmentEvent('CTA Clicked', {
                                isNav: false,
                                type: 'button',
                                text: 'My Favorites',
                                eventContext:
                                  'portal_home_buying_journey_favorites_cta',
                              });
                            }}
                          >
                            My Favorites
                          </Link>
                        </div>
                      </>
                    )}
                  </>
                }
              />
              {dxContext?.state?.account && (
                <>
                  {budgetHomesList &&
                    budgetHomesList !== null &&
                    budgetHomesList.length > 0 && (
                      <RecommendedHomes
                        swiperID="budgetSwiper"
                        heading={`Homes under ${roundedBudgetNumber}`}
                        subHeading="Based on your home budget preferences"
                        listItems={budgetHomesList}
                        homes={homes}
                        dealer={dealer}
                        retailEventLabel="portal_homes_for_you_recommended_homes_budget"
                      />
                    )}
                  {bedBathHomesList &&
                    bedBathHomesList !== null &&
                    bedBathHomesList.length > 0 && (
                      <RecommendedHomes
                        swiperID="bedBathSwiper"
                        heading={bedBathHeading}
                        subHeading="Based on your home features preferences"
                        listItems={bedBathHomesList}
                        homes={homes}
                        dealer={dealer}
                        retailEventLabel="portal_homes_for_you_recommended_homes_beds_baths"
                      />
                    )}
                  {featuresContent &&
                    featuresContent !== null &&
                    featuresContent.map((item, i) => (
                      <React.Fragment key={item.id || 'listitem-' + i}>
                        {item.homesList && item.homesList.length > 0 && (
                          <RecommendedHomes
                            swiperID={item.id}
                            heading={item.headline}
                            subHeading="Based on your home features preferences"
                            listItems={item.homesList}
                            homes={homes}
                            dealer={dealer}
                            retailEventLabel="portal_homes_for_you_recommended_homes_features"
                          />
                        )}
                      </React.Fragment>
                    ))}
                </>
              )}
              {!plannerHasBeenSubmitted && (
                <div className="cta-container">
                  <CTAPlannerCard
                    className="CTAPlannerCard-teal"
                    accentImg={{ src: plannerCTAAccent, alt: 'CTA Icon' }}
                    clickComponent={{ type: '', link: '' }}
                    chip="Your Home Planner"
                    heading="Explore and learn with your home planner"
                    backgroundColor="#007E86"
                    icon={PlannerCTAIcon}
                    components={{
                      Click: () => (
                        <Link
                          to={{
                            pathname: '/portal/planner',
                            state: { dealer, homes },
                          }}
                          onClick={() => {
                            fireSegmentEvent('CTA Clicked', {
                              isNav: false,
                              type: 'card',
                              text: 'planner card link icon',
                              eventContext:
                                'portal_home_buying_journey_planner_cta',
                            });
                          }}
                        >
                          <img
                            src={plannerCardLinkIcon}
                            alt="planner card link icon"
                          />
                        </Link>
                      ),
                    }}
                  />
                </div>
              )}
            </>
          )}
        </HomesForYouStyles>
      </App>
    </ThemeProvider>
  );
};

HomesForYou.getInitialProps = async (ctx) => {
  return await parseInitialPropsContext(ctx, dealerService, homesService).catch(
    (e) => {
      console.error(e);
      return { redirectTo: '/error' };
    }
  );
};

export default HomesForYou;
